import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import {
  ApiResourceTable,
  type ApiResourceExtraColumn,
} from "@project/admin/components/api-resource-table";
import type { Field } from "@project/admin/components/resource-table";
import { gruhUdhyogOrderService } from "@project/services/admin/gruh-udhyog-order.service";
import type { ProductOrder } from "@project/types/gruh-udhyog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import { Loader2, Check, Download } from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { downloadProductOrderReceiptPdf } from "@project/lib/receipt";

const fields: Field[] = [
  {
    name: "id",
    label: "Order Number",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "user_name",
    label: "Customer Name",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "mobile",
    label: "Mobile Number",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "total_amount",
    label: "Total Amount",
    type: "number",
    currency: true,
    showInTable: true,
    readOnly: true,
  },
  {
    name: "created_at",
    label: "Order Date",
    type: "date",
    showInTable: true,
    readOnly: true,
  },
];

function MarkDeliveredCell({
  row,
  refresh,
}: {
  row: ProductOrder;
  refresh: () => void;
}) {
  const [loading, setLoading] = useState(false);

  const handleMarkDelivered = async () => {
    setLoading(true);
    try {
      await gruhUdhyogOrderService.updateStatus(row.id, "Delivered");
      projectToast.success(`Order JU-${row.id} marked as Delivered`);
      refresh();
    } catch (err) {
      projectToast.error(
        getApiErrorMessage(err, "Failed to update order status")
      );
    } finally {
      setLoading(false);
    }
  };

  const status = row.order_status ?? "Pending";
  const isDelivered = status.toLowerCase() === "delivered";
  const isCancelled =
    status.toLowerCase() === "cancelled" || status.toLowerCase() === "canceled";

  if (isDelivered) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
        <Check className="h-4 w-4" /> Delivered
      </span>
    );
  }

  if (isCancelled) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <Button
      size="sm"
      variant="outline"
      disabled={loading}
      onClick={handleMarkDelivered}
      className="h-8 border-amber-200 bg-primary text-white hover:bg-amber-100/50 hover:text-amber-800 dark:border-amber-800/30 dark:bg-amber-950/20 dark:text-amber-400 dark:hover:bg-amber-900/30"
    >
      {loading ? (
        <>
          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          Updating...
        </>
      ) : (
        "Mark Delivered"
      )}
    </Button>
  );
}

const extraColumns: ApiResourceExtraColumn<ProductOrder>[] = [
  {
    id: "items_count",
    label: "Items Count",
    headerClassName: "text-center w-28",
    cellClassName: "text-center w-28",
    render: row => {
      const items = Array.isArray(row.items) ? row.items : [];
      const totalItems = items.reduce(
        (sum, item) => sum + Number(item.quantity ?? 1),
        0
      );
      return <span className="font-semibold">{totalItems}</span>;
    },
  },
  {
    id: "payment_method",
    label: "Payment Method",
    headerClassName: "text-center w-36",
    cellClassName: "text-center w-36",
    render: row => (
      <Badge variant="outline" className="font-medium capitalize">
        {row.payment_method ?? "—"}
      </Badge>
    ),
  },
  {
    id: "order_status",
    label: "Status",
    headerClassName: "text-center w-32",
    cellClassName: "text-center w-32",
    render: row => {
      const status = row.order_status ?? "Pending";
      const isConfirmed = status.toLowerCase() === "confirmed";
      const isDelivered = status.toLowerCase() === "delivered";
      const isCancelled =
        status.toLowerCase() === "cancelled" ||
        status.toLowerCase() === "canceled";
      const badgeClass = isDelivered
        ? "bg-blue-100 text-blue-800 hover:bg-blue-100 border-transparent"
        : isConfirmed
          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-transparent"
          : isCancelled
            ? "bg-red-100 text-red-800 hover:bg-red-100 border-transparent"
            : "bg-amber-100 text-amber-800 hover:bg-amber-100 border-transparent";
      return (
        <Badge
          className={`${badgeClass} text-[10px] font-semibold uppercase tracking-wider`}
        >
          {status}
        </Badge>
      );
    },
  },
  {
    id: "delivery_action",
    label: "Mark Delivered",
    headerClassName: "text-center w-36",
    cellClassName: "text-center w-36",
    render: (row, { refresh }) => (
      <MarkDeliveredCell row={row} refresh={refresh} />
    ),
  },
  {
    id: "receipt_action",
    label: "Receipt",
    headerClassName: "text-center w-32",
    cellClassName: "text-center w-32",
    render: row => (
      <Button
        size="sm"
        variant="outline"
        onClick={e => {
          e.stopPropagation();
          downloadProductOrderReceiptPdf(row);
        }}
        className="h-8 gap-1.5 border-primary/20 text-xs font-semibold text-primary hover:bg-primary/10"
      >
        <Download className="h-3.5 w-3.5" />
        Receipt
      </Button>
    ),
  },
];

export default function ProductOrdersPage() {
  const [selectedOrder, setSelectedOrder] = useState<ProductOrder | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const handleRefresh = () => setRefreshKey(prev => prev + 1);

  const handleUpdateStatusInDialog = async (orderId: number) => {
    setUpdatingStatus(true);
    try {
      const updated = await gruhUdhyogOrderService.updateStatus(
        orderId,
        "Delivered"
      );
      if (updated) {
        setSelectedOrder(updated);
      } else {
        setSelectedOrder(prev =>
          prev ? { ...prev, order_status: "Delivered" } : null
        );
      }
      projectToast.success(`Order JU-${orderId} marked as Delivered`);
      handleRefresh();
    } catch (err) {
      projectToast.error(
        getApiErrorMessage(err, "Failed to update order status")
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Parse items safely if it is stringified JSON or already an array
  const orderItems = (() => {
    if (!selectedOrder?.items) {
      return [];
    }
    if (Array.isArray(selectedOrder.items)) {
      return selectedOrder.items;
    }
    try {
      return JSON.parse(selectedOrder.items);
    } catch {
      return [];
    }
  })();

  return (
    <AdminShell
      title="Product Orders"
      subtitle="View customer order history and statuses"
    >
      <ApiResourceTable<ProductOrder>
        key={refreshKey}
        title="Product Order"
        labelField="id"
        fields={fields}
        extraColumns={extraColumns}
        searchFields={["id", "user_name", "mobile"]}
        defaultValues={{}}
        csvFileName="product-orders"
        list={query => gruhUdhyogOrderService.listPage(query)}
        onView={row => setSelectedOrder(row)}
      />

      <Dialog
        open={selectedOrder !== null}
        onOpenChange={open => {
          if (!open) {
            setSelectedOrder(null);
          }
        }}
      >
        <DialogContent className="max-w-xl rounded-2xl">
          <DialogHeader className="flex flex-row items-center justify-between space-y-0 pr-6">
            <DialogTitle className="text-lg font-bold">
              Order Details: JU-{selectedOrder?.id}
            </DialogTitle>
            {selectedOrder && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadProductOrderReceiptPdf(selectedOrder)}
                className="h-8 gap-1.5 border-primary/30 text-xs font-semibold text-primary hover:bg-primary/10"
              >
                <Download className="h-3.5 w-3.5" /> Download Receipt
              </Button>
            )}
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4 text-sm text-foreground">
              {/* Customer Info */}
              <div className="bg-muted/20 grid grid-cols-2 gap-4 rounded-xl border border-border p-4">
                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Customer Name
                  </span>
                  <span className="mt-0.5 block font-semibold">
                    {selectedOrder.user_name ?? "—"}
                  </span>
                </div>
                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Mobile Number
                  </span>
                  <span className="mt-0.5 block font-semibold">
                    {selectedOrder.mobile ?? "—"}
                  </span>
                </div>
                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Order Status
                  </span>
                  <div className="mt-1 flex items-center gap-2">
                    {(() => {
                      const status = selectedOrder.order_status ?? "Pending";
                      const isConfirmed = status.toLowerCase() === "confirmed";
                      const isDelivered = status.toLowerCase() === "delivered";
                      const isCancelled =
                        status.toLowerCase() === "cancelled" ||
                        status.toLowerCase() === "canceled";
                      const badgeClass = isDelivered
                        ? "bg-blue-100 text-blue-800 hover:bg-blue-100 border-transparent"
                        : isConfirmed
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-transparent"
                          : isCancelled
                            ? "bg-red-100 text-red-800 hover:bg-red-100 border-transparent"
                            : "bg-amber-100 text-amber-800 hover:bg-amber-100 border-transparent";
                      return (
                        <Badge
                          className={`${badgeClass} text-[10px] font-semibold uppercase tracking-wider`}
                        >
                          {status}
                        </Badge>
                      );
                    })()}

                    {(() => {
                      const status = selectedOrder.order_status ?? "Pending";
                      const isDelivered = status.toLowerCase() === "delivered";
                      const isCancelled =
                        status.toLowerCase() === "cancelled" ||
                        status.toLowerCase() === "canceled";
                      if (isDelivered || isCancelled) {
                        return null;
                      }
                      return (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={updatingStatus}
                          onClick={() =>
                            handleUpdateStatusInDialog(selectedOrder.id)
                          }
                          className="h-7 border-amber-200 bg-amber-50/50 px-2.5 py-0 text-xs text-amber-700 hover:bg-amber-100/50 hover:text-amber-800"
                        >
                          {updatingStatus ? (
                            <>
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              Updating...
                            </>
                          ) : (
                            "Mark Delivered"
                          )}
                        </Button>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Total Amount
                  </span>
                  <span className="mt-0.5 block text-base font-bold text-primary">
                    ₹
                    {Number(selectedOrder.total_amount).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              {/* Payment Details */}
              <div className="bg-muted/20 grid grid-cols-2 gap-4 rounded-xl border border-border p-4">
                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Payment Method
                  </span>
                  <span className="mt-0.5 block font-semibold capitalize">
                    {selectedOrder.payment_method ?? "—"}
                  </span>
                </div>
                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Payment Status
                  </span>
                  <Badge
                    className={`mt-1 font-semibold uppercase tracking-wider ${
                      selectedOrder.payment_status === "Paid"
                        ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                        : "bg-amber-100 text-amber-800 hover:bg-amber-100"
                    }`}
                  >
                    {selectedOrder.payment_status ?? "—"}
                  </Badge>
                </div>
                {selectedOrder.razorpay_order_id && (
                  <div className="col-span-2">
                    <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                      Razorpay Order ID
                    </span>
                    <span className="mt-0.5 block break-all font-mono text-xs">
                      {selectedOrder.razorpay_order_id}
                    </span>
                  </div>
                )}
                {selectedOrder.razorpay_payment_id && (
                  <div className="col-span-2">
                    <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                      Razorpay Payment ID
                    </span>
                    <span className="mt-0.5 block break-all font-mono text-xs">
                      {selectedOrder.razorpay_payment_id}
                    </span>
                  </div>
                )}
              </div>

              {/* Items List */}
              <div>
                <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Items ordered
                </h4>
                <div className="overflow-hidden rounded-xl border border-border">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="bg-muted/40 border-b border-border text-xs font-semibold uppercase text-muted-foreground">
                        <th className="p-3">Item / Weight</th>
                        <th className="p-3 text-center">Qty</th>
                        <th className="p-3 text-right">Price</th>
                        <th className="p-3 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {orderItems.map((item: any, idx: number) => {
                        const name =
                          item.product_name ??
                          item.name ??
                          `Product #${item.product}`;
                        const weight = item.weight ? ` (${item.weight})` : "";
                        const qty = Number(item.quantity ?? 1);
                        const price = Number(item.price ?? 0);
                        const subtotal = qty * price;
                        return (
                          <tr
                            key={item.id ?? idx}
                            className="hover:bg-muted/10 text-xs"
                          >
                            <td className="p-3 font-medium">
                              {name}
                              {weight}
                            </td>
                            <td className="p-3 text-center">{qty}</td>
                            <td className="p-3 text-right">
                              ₹{price.toFixed(2)}
                            </td>
                            <td className="p-3 text-right font-semibold">
                              ₹{subtotal.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                      {orderItems.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="p-4 text-center text-xs text-muted-foreground"
                          >
                            No items found in this order.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="bg-muted/20 space-y-1.5 border-t border-border p-3 text-right text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal:</span>
                      <span className="font-medium">
                        ₹{Number(selectedOrder.subtotal ?? 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Delivery Charge:
                      </span>
                      <span className="font-medium">
                        ₹{Number(selectedOrder.delivery_charge ?? 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="border-border/60 flex justify-between border-t pt-1.5 text-sm font-bold text-primary">
                      <span>Total Amount:</span>
                      <span>
                        ₹{Number(selectedOrder.total_amount ?? 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
